import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DIRECTIONS, useSpatialEvent } from 'hooks/use-spatial.js';
import NavArrows from '../nav-arrows/nav-arrows.jsx';
import styles from './slide.module.css';
import { FULLSCREEN } from '../../constants.js';
import { BgLayer } from '../bg-layer.jsx';
import { is } from '../../is.js';
import { bool, func, object } from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import scopeLogger from 'lib/scope-logger.js';
import { DrawingZone } from 'components/DrawingZone';
import { useEventListener, useSetDataAttrs } from '#/hooks/index.js';
import appSlice from '#/store/accessors/app.js';
import { useSelector } from 'react-redux';

const console = scopeLogger('RMI:root:slide');

export default Slide;

Slide.propTypes = {
  slide: object,
  visible: bool,
  onRequestNavigation: func
};

Slide.standard = StandardSlide;
Slide.fullview = FullViewSlide;

/**
 * @param {object} props
 * @param {SlideDescription} props.slide
 * @param {boolean} props.visible
 * @returns {JSX.Element}
 */
function Slide({ slide, visible }) {
  /**
   * @type {React.MutableRefObject<HTMLElement>}
   */
  const ref = useRef();
  const dispatchEvent = useCallback((eventName) => {
    ref.current?.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail: {
          origin: {
            target: slide,
          },
        },
      })
    );
  }, [slide]);

  const onEntering = useCallback(() => {
    dispatchEvent('slide-enter');
  }, [dispatchEvent]);

  const onEntered = useCallback(() => {
    dispatchEvent('slide-view');
    autoFocus(slide.autoFocusWidget);
  }, [dispatchEvent, slide.autoFocusWidget]);

  const onExit = useCallback(() => {
    dispatchEvent('slide-exit');
  }, [dispatchEvent]);

  const onExited = useCallback(() => {
    dispatchEvent('slide-hide');
  }, [dispatchEvent]);

  const SlideView = is.slideDisplayMode.fullscreen(slide)
    ? Slide.fullview
    : Slide.standard;

  const [error, setError] = useState(null);
  useEventListener(
    'error',
    (ev) => {
      const error = new Error('internally slide failure');
      error.source = ev.detail?.error?.message;
      setError(error);
    },
    undefined,
    ref,
  );
  const appIsPaused = useSelector(appSlice.isPaused);

  if (error) {
    // Relly on error boundary
    throw error;
  }

  if (appIsPaused) {
    return null;
  }

  return (
    <CSSTransition
      in={visible}
      appear={true}
      nodeRef={ref}
      classNames={slide.transition.name}
      timeout={slide.transition.timeout}
      onEntering={onEntering}
      onEntered={onEntered}
      onExit={onExit}
      mountOnEnter={true}
      unmountOnExit={true}
    >
      <SlideView slide={slide} ref={ref} beforeUnmount={onExited} />
    </CSSTransition>
  );
}

StandardSlide.propTypes = {
  slide: object,
  beforeUnmount: func,
};

/**
 * @param {Object} props
 * @param {SlideDescription} props.slide
 * @param {function} props.beforeUnmount
 * @param {React.RefObject<HTMLElement>} props.ref
 * @return {JSX.Element}
 */
function StandardSlide(props) {
  const { slide, beforeUnmount, ref} = props;

  useLayoutEffect(() => beforeUnmount, [beforeUnmount]);
  useSetDataAttrs(ref, slide.dataset);

  return (
    <article
      tabIndex={-1}
      ref={ref}
      id={slide.id}
      className={`${styles.standardSlide} ${slide.transition.name}-enter`}
    >
      <DrawingZone zone_id={'safe_zone'}>
        <div className={styles.slideContent}>
          {slide.backgrounds.map((background) => {
            return <BgLayer key={background.id} desc={background} />;
          })}
          {slide.children.map((widget) => {
            return <widget.type key={widget.id} desc={widget} />;
          })}
        </div>
      </DrawingZone>
    </article>
  );
}

FullViewSlide.propTypes = {
  slide: object,
  beforeUnmount: func,
};

/**
 * @param {Object} props
 * @param {SlideDescription} props.slide
 * @param {function} props.beforeUnmount
 * @param {React.MutableRefObject<HTMLElement>} ref
 * @return {JSX.Element}
 */
function FullViewSlide(props) {
  const { slide, beforeUnmount, ref } = props;
  const { displayZone } = slide;
  const className = useMemo(() => {
    return displayZone === 'screen'
      ? styles.fullScreenSlide
      : styles.fullPlayerSlide;
  }, [displayZone]);

  useLayoutEffect(() => beforeUnmount, [beforeUnmount]);

  /* such event possible (at the moment) in STV app ONLY! */
  useSpatialEvent(ref, (ev) => {
    const {
      detail: { candidate: nextElement, direction },
    } = ev;
    console.log(
      `on spatial ${direction}: attempt to delegate focus to element`,
      nextElement,
    );
    /*
    Due to it fullscreen slide we should stop event propagation to prevent lose focus
     */
    ev.stopPropagation();

    if (nextElement) {
      if (nextElement.role === 'slide-arrow') {
        console.log('change slide');
        nextElement.click();
        return;
      }
      nextElement.focus();
      return;
    }

    let slideRef;
    switch (direction) {
      case DIRECTIONS.RIGHT: {
        slideRef = 'next';
        break;
      }
      case DIRECTIONS.LEFT: {
        slideRef = 'prev';
        break;
      }
      default:
        slideRef = 'undefined';
    }
    const slideArrow = ref.current.querySelector(
      `a[role="slide-arrow"][href="megogo://slide/${slideRef}"]`,
    );
    if (slideArrow && slideArrow.click) {
      console.log('apply slide navigation');
      slideArrow.click();
    }
  });

  useSetDataAttrs(ref, slide.dataset);

  return (
    <article
      tabIndex={-1}
      ref={ref}
      id={slide.id}
      className={`${className} ${slide.transition.name}-enter`}
    >
      {slide.backgrounds.map((background) => {
        return <BgLayer key={background.id} desc={background} />;
      })}

      <div className={styles.slideContent} data-display-mode={FULLSCREEN}>
        {slide.children.map((widget) => {
          return <widget.type key={widget.id} desc={widget} />;
        })}
        {slide.parent.isArrows && <NavArrows slide={slide} />}
      </div>
    </article>
  );
}

function autoFocus(elementID) {
  if (!document.hasFocus()) {
    console.log('omit focus on element due to document not in focus');
    return;
  }

  const autoFocusable = document.getElementById(elementID);
  autoFocusable.focus({ preventScroll: true });
  console.log('apply autofocus on slide to element', autoFocusable);

  /**
   * На случай отсутсвия поддержки preventScroll
   * Не очень красиво, но работает.
   *
   * не работает data-name="poll" и приехали
   */
  // const scrollParent = document.querySelector('section[data-name=promo]');
  // scrollParent.scrollTo(0, 0);
}
